<template>
  <div class="education-detail-page">
    <PageHeader
      v-if="!catalog_loading"
      :title="item.name || ''"
      class="education-details-page-header"
      icon="uil-book-open"
    />

    <!-- LOADING BOX -->
    <EducationDetailsSkeleton v-if="catalog_loading" />
    <!-- DETAILS -->
    <div v-show="!catalog_loading" class="ed-container">
      <div class="ed-layout">
        <!-- Sidebar -->
        <div class="uk-width-1-3@m uk-flex-first">
          <div class="ed-sidebar-card">
            <!-- Cover Image -->
            <div class="ed-sidebar-image">
              <img :alt="item.name" :src="item.cover_image_link" />
            </div>

            <!-- Card Body -->
            <div class="ed-sidebar-body">
              <!-- Progress -->
              <div v-if="item.has_been_assigned" class="ed-progress-section">
                <div class="ed-progress-header">
                  <span class="ed-progress-label">%{{ checkCompletion(item.completion_percentage) }} {{
                    $t("education.Completed") }}</span>
                </div>
                <div class="ed-progress-bar">
                  <div class="ed-progress-fill" :class="{
                    'bg-success': item.completion_percentage >= 75,
                    'bg-warning': item.completion_percentage >= 50 && item.completion_percentage < 75
                  }" :style="'width:' + checkCompletion(item.completion_percentage) + '%'"
                    :uk-tooltip="'title:%' + checkCompletion(item.completion_percentage) + ' ' + $t('education.Completed') + '; pos: top-center'">
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="ed-actions">
                <template v-if="item.has_been_assigned">
                  <a v-if="!item.is_started && !item.is_completed" class="ed-btn ed-btn--danger" href="#"
                    @click.prevent="startFirstEducation">
                    <i class="uil-forward"></i>
                    <span>{{ $t("education.Start_Education") }}</span>
                  </a>
                  <a v-else-if="item.is_started && !item.is_completed" class="ed-btn ed-btn--success"
                    href="javascript:void(0)" @click.prevent="startFromUnlockedEducation">
                    <i class="uil-forward"></i>
                    <span>{{ $t("education.Continue_Where_I_Stay") }}</span>
                  </a>
                  <a v-else-if="item.is_started && item.is_completed" class="ed-btn ed-btn--completed"
                    href="javascript:void(0)">
                    <i class="uil-check-circle"></i>
                    <span>{{ $t("general.completed") }}</span>
                  </a>
                  <a class="ed-btn ed-btn--outline" @click="addCollection">
                    <i class="uil-brightness-plus"></i>
                    <span>{{ $t("education.Add_collection") }}</span>
                  </a>
                </template>
                <template v-else>
                  <template v-if="!item.self_registrable_trainings || item.self_registrable_trainings.length === 0">
                    <!-- Request Button -->
                    <button v-if="item.user_request == null && item?.id" type="button" class="ed-btn ed-btn--ghost"
                      @click="requestForTraining(item.id)">
                      <i class="uil-bell"></i>
                      <span>{{ $t("general.request") }}</span>
                    </button>

                    <!-- Requested -->
                    <button v-else-if="item.user_request &&
                      Number(item.user_request.training_status_id) === 1
                      " type="button" class="ed-btn ed-btn--primary disabled" disabled>
                      <i class="fa fa-check"></i>
                      <span>{{ $t('general.requested') }}</span>
                    </button>

                    <!-- Rejected -->
                    <button v-else-if="item.user_request &&
                      Number(item.user_request.training_status_id) === 0
                      " type="button" class="ed-btn ed-btn--danger disabled" disabled>
                      <span>{{ $t('general.reject_request') }}</span>
                    </button>
                  </template>

                  <!-- Self Registrable Trainings -->
                  <template v-else-if="item.self_registrable_trainings &&
                    item.self_registrable_trainings.length > 0
                    ">
                    <button v-if="!item.user_self_register_training?.registered_at" type="button"
                      class="ed-btn ed-btn--success" @click="requestForSelfTraining(item.id)">
                      <i class="uil-check-circle"></i>
                      <span>{{ $t('general.sign_up') }}</span>
                    </button>

                    <!-- Already Registered -->
                    <button v-else type="button" class="ed-btn ed-btn--primary disabled" disabled>
                      <i class="fa fa-check"></i>
                      <span>{{ $t('general.registered') }}</span>
                    </button>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="uk-width-2-3@m">
          <!-- Hero Card -->
          <div class="ed-header">
            <div class="ed-hero">
              <span v-if="item.training_type" class="ed-badge ed-badge--secondary">
                {{ item.training_type.name }}
              </span>
              <h1 class="ed-title">{{ item.name }}</h1>
              <div class="ed-meta-row">
                <div class="ed-meta-item">
                  <i class="icon-feather-users"></i>
                  {{ item.users_completed_count }}
                  {{ $t("education.Person_Completed_Training") }}
                </div>
              </div>
            </div>
          </div>

          <!-- Alerts -->
          <div v-if="successMessage != null" class="ed-alert ed-alert--success">
            <i class="fa fa-check"></i>
            <span>{{ successMessage }}</span>
            <span class="ed-alert-close" @click="successMessage = null">&times;</span>
          </div>

          <div v-if="errorMessage != null" class="ed-alert ed-alert--danger">
            <span>{{ errorMessage }}</span>
            <span class="ed-alert-close" @click="errorMessage = null">&times;</span>
          </div>

          <!-- Tab Wrapper -->
          <div class="ed-tab-wrapper">
            <nav class="ed-tab-nav">
              <ul
                uk-switcher="connect: #course-intro-tab ;animation: uk-animation-slide-right-medium, uk-animation-slide-left-medium">
                <li>
                  <a href="#"><i class="uil-list-ul"></i>
                    <span>{{ $t("education.descriptions") }}</span></a>
                </li>
                <li>
                  <a href="#"><i class="uil-comment-info-alt"></i>
                    <span>{{ $t("education.Sharing_Experience") }}</span></a>
                </li>
              </ul>
            </nav>
            <ul id="course-intro-tab" class="ed-tab-content uk-switcher">
              <!-- Contents -->
              <li>
                <div class="ed-section-header">
                  <h4>{{ $t("education.about") }}</h4>
                </div>
                <p class="ed-description">{{ item.description }}</p>
                <div v-if="item.hashtag && item.hashtag.length" class="ed-hashtags">
                  <span v-for="(tag, index) in item.hashtag" :key="index" class="ed-hashtag">#{{ tag.name }}</span>
                </div>
                <AssignmentCollection :assignment-id="$route.params.id" :educations="collectionEducations"
                  :visible="is_add_collection" collection-content-url="api/training/collection-content/"
                  @close="is_add_collection = false" @error="errorMessage = $event" @success="successMessage = $event" />

                <ul class="course-curriculum" uk-accordion="multiple: true">
                  <li v-for="(subItem, index) in item.training_section" :key="index" :class="open_section_id == subItem.id ||
                    (open_section_id == null && index == 0)
                    ? 'uk-open'
                    : ''
                    ">
                    <a class="uk-accordion-title" href="#"> {{ subItem.name }}</a>
                    <div class="uk-accordion-content">
                      <ul class="course-curriculum-list">
                        <template v-for="(trainingContent, subIndex) in subItem.training_content">
                          <ContentItem v-if="trainingContent.file_category" :key="'item_' + subIndex"
                            :has_been_assigned="false" :icon="trainingContent.file_category.icon"
                            :is_completed="item.unlocked_items.includes(trainingContent.id) ? true : false"
                            :is_demo="trainingContent.is_demo"
                            :is_locked="(item.has_been_assigned != true || item.locked_items.includes(trainingContent.id)) && !item.user_self_register_training ? true : false"
                            :is_unlocked="item.has_been_assigned == true && item.current_item == trainingContent.id ? true : false"
                            :name="$t(trainingContent.file_category.key_name)" :point="trainingContent.score"
                            :title="trainingContent.name"
                            @linkClick="showContentModal(trainingContent, trainingContent.completed_by_user)">
                          </ContentItem>
                        </template>
                      </ul>
                    </div>
                  </li>
                </ul>
                <div id="training_content_modal" bg-close="false" class="p-0 ed-modal-wrapper" esc-close="false"
                  style="z-index: 9999999; background:#dddddd;" uk-modal>
                  <div v-if="selected_content != null" class="uk-modal-dialog bg-transparent ed-modal-dialog"
                    style="width: 100%">
                    <div v-if="!isScormContent" class="ed-modal-header">
                      <button class="ed-close-btn uk-modal-close-default" type="button" uk-close></button>
                      <h2 class="ed-modal-title">{{ selected_content.name }}</h2>
                    </div>
                    <button v-else class="ed-close-btn uk-modal-close-default" type="button" uk-close></button>
                    <div class="uk-modal-body py-0 ed-modal-body" :class="{ 'ed-modal-body--fullscreen': isScormContent }"
                      style="overflow: auto;">
                      <!-- Resim (type 2) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.visualDocument"
                        class="video-responsive">
                        <img :src="selected_content.file.file_link" class="modal-image-preview" />
                      </div>
                      <!-- Audio (type 6), Video (type 7), Video URL (type 8) -->
                      <EducationVideoPlayer v-if="[
                        CATEGORY_IDS.audioDocument,
                        CATEGORY_IDS.videoDocument,
                        CATEGORY_IDS.videoUrl
                      ].includes(selected_content.training_file_type_id)" ref="videoPlayer" :isDemo="demo_status"
                        :selected_content="selected_content" :trainingId="trainingId" @video-ended="onVideoEnded" />
                      <!-- PDF (type 3) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.pdfDocument"
                        class="ed-content-frame">
                        <PdfViewer :src="selected_content.file.file_link" mode="scroll" />
                      </div>
                      <!-- Word / PowerPoint (type 4, 5) -->
                      <div v-if="[
                        CATEGORY_IDS.wordDocument,
                        CATEGORY_IDS.powerpointDocument
                      ].includes(selected_content.training_file_type_id)" class="ed-content-frame">
                        <iframe :src="fixedPptWordFile"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; camera; microphone"
                          allowfullscreen frameborder="0" height="500" mozallowfullscreen scrolling="no"
                          webkitallowfullscreen width="1000">
                        </iframe>
                      </div>
                      <!-- URL (type 9) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.url"
                        class="url-content-container">
                        <div class="url-content-info">
                          <i class="uil-link-alt url-icon"></i>
                          <p class="url-description"> {{ $t('general.url_external_info') }} </p>
                          <a :href="selected_content.training_content" class="btn btn-primary btn-lg"
                            rel="noopener noreferrer" target="_blank">
                            <i class="uil-external-link-alt mr-2"></i> {{ $t('general.go_to_link') }}
                          </a>
                        </div>
                      </div>
                      <!-- Bilgi İçeriği (type 17) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.informationContent"
                        class="info-content-container">
                        <div class="info-content-box">
                          <i class="uil-book-open info-icon"></i>
                          <h3 class="info-title">{{ selected_content.name }}</h3>
                          <p v-if="selected_content.training_content" class="info-description">
                            {{ selected_content.training_content }}</p>
                          <p class="info-note">{{ $t('general.information_content_info') }}</p>
                        </div>
                      </div>
                      <!-- Webinar (type 18) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.webinar"
                        class="webinar-content-container">
                        <div class="webinar-content-info">
                          <i class="uil-video webinar-icon"></i>
                          <h3 class="webinar-title">{{ selected_content.name }}</h3>
                          <p class="webinar-description">{{ $t('general.webinar_info') }}</p>
                          <a :href="selected_content.training_content" class="btn btn-success btn-lg"
                            rel="noopener noreferrer" target="_blank">
                            <i class="uil-video mr-2"></i> {{ $t('general.join_webinar') }}
                          </a>
                        </div>
                      </div>
                      <!-- Anzera (type 10) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.anzeraEducation"
                        class="ed-content-frame">
                        <iframe :src="anzeraPackageItemUrl" :title="selected_content.title" frameborder="0" height="100vh"
                          width="100vw">
                        </iframe>
                      </div>
                      <!-- SCORM (type 1) -->
                      <div v-if="selected_content.training_file_type_id == CATEGORY_IDS.scormPackage"
                        class="ed-content-frame">
                        <iframe :src="scormPackageItemUrl" :title="selected_content.title" frameborder="0" height="100vh"
                          width="100vw">
                        </iframe>
                      </div>
                    </div>

                    <!-- Video URL içerikleri için manuel tamamlama butonu -->
                    <div v-if="selected_content && selected_content.training_file_type_id === CATEGORY_IDS.videoUrl"
                      class="ed-modal-footer">
                      <button class="btn btn-primary training-complete-btn" type="button" @click="onManualVideoComplete">
                        <i class="uil-check-circle"></i>
                        {{ $t('education.completed_the_content') || 'İçeriği tamamladım' }}
                      </button>
                    </div>
                  </div>
                </div>
              </li>

              <!-- Feedbacks -->
              <li>
                <div class="ed-section-header">
                  <h4>
                    {{ $t('general.experience_sharing') }}
                    <span class="ed-feedback-count">({{ item.approved_comments ? item.approved_comments.length : 0 }}
                      {{ $t("education.Sharing") }})</span>
                  </h4>
                </div>

                <!-- Comments List -->
                <div class="ed-feedback-list">
                  <div v-for="(comment, index) in item.approved_comments" :id="'comment_' + comment.id"
                    :key="'comment_index_' + index" class="ed-feedback-item">
                    <div class="ed-feedback-avatar">
                      <img :src="comment.user.photo_link" alt="" />
                    </div>
                    <div class="ed-feedback-content">
                      <div class="ed-feedback-header">
                        <span class="ed-feedback-name">{{ comment.user.name }} {{ comment.user.surname }}</span>
                        <span v-if="comment.user.position?.name" class="ed-feedback-position">{{
                          comment.user.position.name }}</span>
                      </div>
                      <p class="ed-feedback-text">
                        {{ comment.text }}
                        <span v-if="comment.status_id === 1" class="ed-feedback-pending">
                          ({{ $t('general.sent_to_admin_approval') }})
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- New Comment Form -->
                <div class="ed-feedback-form">
                  <div class="ed-feedback-avatar">
                    <img :src="this.authUser.photo" alt="" />
                  </div>
                  <div class="ed-feedback-input-wrapper">
                    <textarea v-model="comment_text" :placeholder="$t('general.write_your_experiences_here')"
                      class="ed-feedback-textarea" rows="4"></textarea>
                    <div class="ed-feedback-actions">
                      <button class="ed-btn ed-btn--primary ed-btn--sm" type="button" @click="makeComment">
                        <i class="uil-message"></i>
                        {{ $t("messages.send") }}
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Comments from "@/view/components/educationDetail/Comments.vue";
import ContentItem from "@/view/components/assignmentDetail/ContentItem.vue";
import { mapGetters } from 'vuex'
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import EducationVideoPlayer from "@/view/components/video/education-video/education-video-player.vue";
import { CATEGORY_IDS } from "@/core/constant/content-types";

import {
  GET_ITEM_DETAIL_BY_ID,
  UPDATE_ITEM_BY_ID,
  CREATE_ITEM,
  ERROR as REST_ERROR,
  LOADING as REST_LOADING
} from "@/core/services/store/REST.module";
import AssignmentCollection from "@/view/User/AssignmentDetails/components/AssignmentCollection.vue";
import EducationDetailsSkeleton from "@/view/User/EducationDetails/EducationDetailsSkeleton.vue";
import PdfViewer from "@/view/components/ui/PdfViewer.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import bridge from "@/core/services/bridge.service";

export default {
  name: "EducationDetails",
  components: {
    Comments,
    AssignmentCollection,
    ContentItem,
    EducationDetailsSkeleton,
    DefaultLoading,
    EducationVideoPlayer,
    PdfViewer,
    PageHeader
  },
  data() {
    return {
      restUrl: "api/training/",
      scormUrl: "api/training-content/get-scorm/",
      commentUrl: "api/training/comment/",
      anzeraUrl: "api/training/anzera/",
      completeContentUrl: "api/training/complete-content/",
      trainingRequestUrl: "api/training/request/",
      startContentUrl: "api/training/start-content/",
      is_add_collection: false,
      scormPackageItemUrl: null,
      unlocked_education: null,
      anzeraPackageItemUrl: "",
      successMessage: null,
      errorMessage: null,
      item: [],
      comment_text: '',
      open_section_id: null,
      selected_content: null,
      trainingId: null,
      demo_status: true,
      fixedPptWordFile: "",
      suppressModalCloseEffects: false,
      CATEGORY_IDS,
    };
  },
  computed: {
    ...mapGetters({
      catalog_loading: REST_LOADING
    }),

    restError: {
      get() {
        return this.$store.getters[REST_ERROR];
      },
      set(value) {
      },
    },
    isScormContent() {
      if (!this.selected_content) return false;
      const id = this.selected_content.training_file_type_id;
      return id === CATEGORY_IDS.scormPackage || id === CATEGORY_IDS.anzeraEducation;
    },
    collectionEducations() {
      if (!this.item?.training_section) return [];
      return this.item.training_section.flatMap(section =>
        section.training_content.map(c => ({ id: c.id, title: c.name }))
      );
    },
  },

  methods: {
    resetSelectedContentState() {
      this.selected_content = null;
      this.scormPackageItemUrl = null;
      this.anzeraPackageItemUrl = "";
      this.fixedPptWordFile = "";
    },

    resetDetailState() {
      this.item = [];
      this.resetSelectedContentState();
      this.unlocked_education = null;
      this.open_section_id = null;
      this.demo_status = true;
      this.is_add_collection = false;
      this.comment_text = "";
      this.successMessage = null;
      this.errorMessage = null;
    },

    closeContentModalSilently() {
      if (!this.selected_content) return;

      this.suppressModalCloseEffects = true;

      if (typeof UIkit !== "undefined") {
        const modal = UIkit.modal("#training_content_modal");
        if (modal) {
          modal.hide();
          return;
        }
      }

      this.suppressModalCloseEffects = false;
    },

    loadTrainingFromRoute(trainingId) {
      if (!trainingId) {
        this.$router.go(-1);
        return;
      }

      this.closeContentModalSilently();
      this.trainingId = trainingId;
      this.resetDetailState();
      this.getTrainingItem(trainingId);
    },


    requestForSelfTraining(item_id) {
      this.$store
        .dispatch(UPDATE_ITEM_BY_ID, {
          url: 'api/trainings/' + item_id + '/self-registrable',
        })
        .then(() => {
          window.location.reload();
        });
    },

    checkCompletion(percentage) {
      let completionPercentage = 0
      completionPercentage = percentage >= 100 ? 100 : percentage
      return completionPercentage
    },
    setUnlockedEducation() {
      if (this.item.has_been_assigned == false) return;
      this.unlocked_education = null;
      this.open_section_id = null;
      this.item.training_section.forEach((section) => {
        section.training_content.forEach((content) => {
          if (this.unlocked_education != null) return;
          if (!content.completed_by_user && content.is_demo == false) {
            this.open_section_id = section.id;
            this.unlocked_education = content.id;
            return false;
          }
        });
      });
    },
    startFirstEducation() {
      let item = this.item;
      this.showContentModal(item.training_section[0].training_content[0], false);
      return true;
    },
    showContentModal(selected_content, is_completed) {
      this.selected_content = selected_content;

      this.demo_status = !this.item.has_been_assigned && selected_content.is_demo
      if ([
        CATEGORY_IDS.wordDocument,
        CATEGORY_IDS.powerpointDocument
      ].includes(selected_content.training_file_type_id) && selected_content.file?.file_link) {
        this.fixedPptWordFile =
          "https://view.officeapps.live.com/op/embed.aspx?src=" +
          encodeURIComponent(selected_content.file.file_link) +
          "?postMessageEvents=true";
      }
      if (this.demo_status) {
        UIkit.modal("#training_content_modal").show();
        return false;
      }
      this.setStartedAt();
      this.startContent(selected_content);

      if ([
        CATEGORY_IDS.wordDocument,
        CATEGORY_IDS.powerpointDocument
      ].includes(selected_content.training_file_type_id)) {
        this.completeContent(selected_content);
        this.getTrainingItem()
        UIkit.modal("#training_content_modal").show();
        return true;
      }

      if (selected_content.training_file_type_id === CATEGORY_IDS.scormPackage) {
        this.getScormEducationUrl(selected_content.id);
      }

      if (selected_content.training_file_type_id == CATEGORY_IDS.anzeraEducation) {
        this.getAnzeraEducationUrl(selected_content.id);
      }

      UIkit.modal("#training_content_modal").show();
    },
    startFromUnlockedEducation() {
      // current_item'ı training_section içinde ara
      let foundContent = null;

      for (let i = 0; i < this.item.training_section.length; i++) {
        let section = this.item.training_section[i];
        for (let j = 0; j < section.training_content.length; j++) {
          let content = section.training_content[j];
          if (content.id === this.item.current_item) {
            foundContent = content;
            break;
          }
        }
        if (foundContent) break;
      }

      if (foundContent) {
        this.showContentModal(foundContent, false);
      } else {
        // current_item bulunamadıysa, ilk açık içeriği bul
        let firstUnlockedContent = this.findFirstUnlockedContent();
        if (firstUnlockedContent) {
          this.showContentModal(firstUnlockedContent, false);
        } else {
          // Hiçbir şey bulunamadıysa ilk içeriği aç
          let sections = this.item.training_section;
          if (sections.length > 0 && sections[0].training_content.length > 0) {
            this.showContentModal(sections[0].training_content[0], false);
          }
        }
      }
      return true;
    },
    findFirstUnlockedContent() {
      // training_section içinde ilk açık (kilitli olmayan) içeriği bul
      for (let i = 0; i < this.item.training_section.length; i++) {
        let section = this.item.training_section[i];
        for (let j = 0; j < section.training_content.length; j++) {
          let content = section.training_content[j];
          // Kilitli olmayan ilk içeriği bul
          if (!this.item.locked_items.includes(content.id)) {
            return content;
          }
        }
      }
      return null;
    },

    makeComment() {
      if (this.comment_text.length < 2) {
        this.errorMessage = this.$t("validation.required_field").replace(
          ":NAME",
          this.$t("assignment.comment")
        );
        return false;
      }
      let formData = new FormData();
      formData.append("comment", this.comment_text);
      formData.append("by_user", true);
      this.$store
        .dispatch(UPDATE_ITEM_BY_ID, {
          url: this.commentUrl + this.trainingId,
          contents: formData,
        })
        .then((result) => {
          if (!this.restError) {
            this.comment_text = "";
            if (Object.keys(result.data).length)
              this.item.approved_comments.unshift(result.data);
            this.successMessage = this.$t("general.successfully_created");
          } else this.errorMessage = this.restError;
        });
    },
    setStartedAt() {
      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: this.restUrl + "started/" + this.trainingId,
        contents: {
          by_user: true,
          type: "started",
        },
      });
    },
    setCompletedAt() {
      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: this.restUrl + "completed/" + this.trainingId,
        filters: {
          by_user: true
        },
        contents: {
          type: "completed",
        },
      });
    },
    getScormEducationUrl(content_id) {
      const url = this.scormUrl + this.trainingId + "/training-content/" + content_id;
      this.$store
        .dispatch(GET_ITEM_DETAIL_BY_ID, {
          url: url,
          acceptPromise: false,
          filters: {
            by_user: true
          },
        })
        .then((result) => {
          if (result.status) {
            this.scormPackageItemUrl = result.data;
          }
        });
    },
    getAnzeraEducationUrl(content_id) {
      const url = this.anzeraUrl + this.trainingId + "/content/" + content_id;
      this.$store
        .dispatch(GET_ITEM_DETAIL_BY_ID, {
          url: url,
          acceptPromise: false,
          filters: {
            by_user: true
          },
        })
        .then((result) => {
          if (result.status) {
            this.anzeraPackageItemUrl = result.data;
          }
        });
    },
    getSectionContentIndex(edu_id) {
      let result = null;
      this.item.training_section.forEach((section, index) => {
        var content_index = section.training_content.findIndex(
          (content) => content.id == edu_id
        );
        if (content_index != -1) {
          result = { content_index: content_index, section_index: index };
          return result;
        }
      });
      return result;
    },
    setModalListener() {
      const self = this;

      // Modal açıkken geri tuşuna basılırsa önce modal kapanır (web & mobil uyumlu)
      this._contentModalHistoryState = null;
      this._contentModalPopstateHandler = function () {
        if (self._contentModalHistoryState && self.selected_content) {
          self._contentModalHistoryState = null;
          UIkit.modal("#training_content_modal").hide();
        }
      };
      window.addEventListener("popstate", this._contentModalPopstateHandler);

      $("#training_content_modal").on({
        "show.uk.modal": function () {
          // Modal açılınca orientation kilidini kaldır (yan çevirme aktif)
          bridge.send('ORIENTATION_LOCK', { orientation: 'default' });

          // Geri tuşu desteği: history state ekle
          self._contentModalHistoryState = { contentModal: true, id: Date.now() };
          history.pushState(self._contentModalHistoryState, "");
        },

        "hide.uk.modal": function () {
          // Route değişimi sırasında modal sessizce kapatılıyorsa, side-effect'leri atla
          if (self.suppressModalCloseEffects) {
            self.suppressModalCloseEffects = false;
            self._contentModalHistoryState = null;
            bridge.send('ORIENTATION_LOCK', { orientation: 'portrait' });
            self.resetSelectedContentState();
            return false;
          }

          // Geri tuşu desteği: history state temizle
          if (self._contentModalHistoryState && history.state && history.state.contentModal && history.state.id === self._contentModalHistoryState.id) {
            history.back();
          }
          self._contentModalHistoryState = null;

          // Orientation kilidini geri al (portrait'e dön)
          bridge.send('ORIENTATION_LOCK', { orientation: 'portrait' });

          if (self.demo_status) {
            self.resetSelectedContentState();
            return false;
          }

          // selected_content null kontrolü
          if (!self.selected_content) {
            return false;
          }

          const contentTypeId = +self.selected_content.training_file_type_id;
          const isVideoContent = [6, 7, 8].includes(contentTypeId);

          // Video player'a modal kapandığını bildir
          if (isVideoContent) {
            self.$refs.videoPlayer?.onModalClose?.();
            self.getTrainingItem();
          }

          // SCORM (type 1)
          if (contentTypeId === 1) {
            self.getTrainingItem();
          }

          // Video/audio dışındaki içerikler için tamamlama
          if (!isVideoContent) {
            self.completeContent(self.selected_content);
          }

          if (self.selected_content.training_file_type_id) {
            self.getTrainingItem();
          }

          self.resetSelectedContentState();
        },
      });
    },
    onVideoEnded(content) {
      this.completeContent(content);
    },
    onManualVideoComplete() {
      if (!this.selected_content || this.selected_content.training_file_type_id !== this.CATEGORY_IDS.videoUrl) {
        return;
      }
      this.completeContent(this.selected_content);
      try {
        if (typeof UIkit !== 'undefined') {
          const modal = UIkit.modal("#training_content_modal");
          if (modal) modal.hide();
        }
      } catch (e) {
        // UIkit yoksa sessizce yoksay
      }
    },
    completeContent(education) {
      const formData = new FormData();
      formData.append("content_id", education.id);
      formData.append("by_user", true);
      this.$store
        .dispatch(UPDATE_ITEM_BY_ID, {
          url: this.completeContentUrl + this.trainingId + "/" + education.id,
          contents: formData,
        })
        .then((result) => {
          if (!this.restError && result.status) {
            const data = result.data;
            const content_section_index = this.getSectionContentIndex(education.id);
            this.item.training_section[
              content_section_index.section_index
            ].training_content[
              content_section_index.content_index
            ].completed_by_user = 1;
            this.item.completion_percentage = data.completion_percentage;
            this.item.is_completed = data.is_completed;

            this.item.user_content_completed = data.user_content_completed;
            this.item.user_content_completed_count = data.user_content_completed_count;
            this.item.locked_items = data.locked_items;
            this.item.unlocked_items = data.unlocked_items;
            this.item.current_item = data.current_item;

            if (this.item.is_completed === true) {
              this.setCompletedAt();
            }
          } else {
            this.errorMessage = this.restError;
          }
        });
    },
    startContent(education) {
      if (this.item.is_expired === true) return false;
      const formData = new FormData();

      formData.append("content_id", education.id);
      formData.append("by_user", true);

      this.$store
        .dispatch(UPDATE_ITEM_BY_ID, {
          url: this.startContentUrl + this.trainingId + "/" + education.id,
          contents: formData,
        })
        .then((result) => {
          if (!this.restError && result.status) {
            const data = result.data;
            this.item.completion_percentage = data.completion_percentage;
          } else {
            this.errorMessage = this.restError;
          }
        });
    },
    requestForTraining(id) {
      this.$store
        .dispatch(CREATE_ITEM, {
          url: this.trainingRequestUrl + id,
          acceptPromise: true,
        })
        .then((result) => {
          if (result.status) {
            this.item.user_request = result.data;
          }
        });
    },
    addCollection() {
      this.is_add_collection = true;
    },
    resetMessages() {
      this.errorMessage = null;
      this.successMessage = null;
    },
    getTrainingItem(trainingId = this.trainingId) {
      if (!trainingId) {
        this.$router.go(-1);
        return;
      }

      this.$store
        .dispatch(GET_ITEM_DETAIL_BY_ID, {
          url: this.restUrl + trainingId,
          acceptPromise: false,
          showLoading: true,
          filters: {
            by_user: true
          },
        })
        .then((result) => {
          if (result.status) {
            this.item = result.data;
          }
        });
    }
  },
  mounted() {
    this.loadTrainingFromRoute(this.$route.params.id);
    this.setModalListener();
  },
  beforeRouteUpdate(to, from, next) {
    if (to.params.id !== from.params.id) {
      this.loadTrainingFromRoute(to.params.id);
    }
    next();
  },
  beforeDestroy() {
    // Geri tuşu listener'ını temizle
    if (this._contentModalPopstateHandler) {
      window.removeEventListener("popstate", this._contentModalPopstateHandler);
    }
    if (this._contentModalHistoryState && history.state && history.state.contentModal && history.state.id === this._contentModalHistoryState.id) {
      history.back();
    }
    this._contentModalHistoryState = null;

    const modal = UIkit.modal("#training_content_modal");
    if (modal) modal.$destroy(true);
  },
  watch: {
    item: {
      handler(val) {
        if (val && this.item.has_been_assigned) this.setUnlockedEducation();
      },
      deep: true,
    },
    successMessage() {
      this.debouncedResetValues();
    },
    errorMessage() {
      this.debouncedResetValues();
    },
  },
  created: function () {
    this.debouncedResetValues = _.debounce(this.resetMessages, 3000);
  },

};
</script>

<style src="./EducationDetails.css"></style>
